import React, { useContext } from "react"
import useSSR from "use-ssr"
import {
	Flex,
	Accordion,
	useAccordionActiveAccordion,
	Button,
	breakpoints,
} from "@capioweb/ui-components"
import {
	Property,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout, TwoColumnGridLayout } from "../../utils/layout"
import { FaqAccordionItem, FaqAccordionBaseBlockItem } from "./FaqAccordionItem"
import { AnchorLinkContext, LocalesContext } from "../../context"
import { useSetCols, useHandleHashChange } from "../../hooks"

type CapioNexusOurOfferContentAreaProperty = Property<
	FaqAccordionBaseBlockItem[]
>
interface CapioNexusFaqSectionBlockProps {
	heading: StringProperty
	contentArea: CapioNexusOurOfferContentAreaProperty
	anchorId?: StringProperty
}

const CapioNexusFaqSectionBlock: React.FC<CapioNexusFaqSectionBlockProps> = ({
	contentArea,
	anchorId,
	...rest
}) => {
	const { isServer } = useSSR()
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const anchorLinkContext = useContext(AnchorLinkContext)
	const maxItemsPerCol = 6
	const [collapsed, setCollapsed] = React.useState(true)
	const { cols } = useSetCols(contentArea.value ?? [])

	// Listen for hash on popState and update active accordion ID
	const { activeId, setActiveId } = useAccordionActiveAccordion(null)
	useHandleHashChange(setActiveId)

	React.useEffect(() => {
		if (isServer) return

		if (contentArea?.value && window.innerWidth > breakpoints.l) {
			if (contentArea.value.length <= maxItemsPerCol * 2) {
				setCollapsed(false)
			}
		}
	}, [setCollapsed])

	React.useEffect(() => {
		setActiveId(anchorLinkContext.activeId)
	}, [])

	return (
		<BlockCommonLayout id={anchorId?.value || null} {...rest}>
			{contentArea && (
				<Flex w={1} flexDirection="column" alignItems="center">
					<TwoColumnGridLayout
						items={cols.map((col, i) => (
							<Accordion key={`acc-${i}`} activeId={activeId} controlled>
								{col.map((item, colI) => (
									<FaqAccordionItem
										{...item}
										hidden={
											collapsed && colI + 1 > maxItemsPerCol ? true : false
										}
										setActiveId={setActiveId}
										activeId={activeId}
										key={colI}
									/>
								))}
							</Accordion>
						))}
					/>
					<Button
						display={collapsed ? "inline-flex" : "none"}
						variant="ghost"
						textColor="navy"
						icon="plus"
						onClick={() => setCollapsed(false)}
						mt={{ _: 2, l: 5 }}
					>
						{translate(translationKeys.showMore) &&
							translate(translationKeys.showMore)}
					</Button>
				</Flex>
			)}
		</BlockCommonLayout>
	)
}

export { CapioNexusFaqSectionBlock }
